import cn from 'classnames';
import isEmpty from 'lodash/isEmpty';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroller';

import { getSearchResults } from '../api/Search';
import { PrimaryButton } from '../components/Buttons/Buttons';
import CategoriesAside from '../components/CategoriesAside/CategoriesAside';
import CategoriesBanner from '../components/CategoriesBanner/CategoriesBanner';
import CategoriesSelect from '../components/CategoriesSelect/CategoriesSelect';
import CustomMetaHead from '../components/CustomMetaHead';
import DeliveryBanner from '../components/DeliveryBanner/DeliveryBanner';
import FavoriteAside from '../components/FavoritesAside/FavoritesAside';
import Footer from '../components/Footer/Footer';
import Header from '../components/Header/Header';
import Navigation from '../components/Navigation/Navigation';
import OfferList from '../components/ProductsMenu/OfferList';
import ProductListItem from '../components/ProductsMenu/ProductListItem';
import listTitle from '../components/ProductsMenu/ProductMenu.module.scss';
import META_DATA from '../constants/metaData';
import CartStore, { productsQuantities } from '../store/cart';
import CategoryStore from '../store/categories';
import DeliveryMode from '../store/deliveryMode';
import Loader from '../store/loader';
import ProductsSearchStore from '../store/productsSearch';
import SortBy from '../store/productsSort';
import BANNER_SORT_OPTIONS from '../utils/bannerOptions';
import { isBrowser } from '../utils/browser';
import { getOrFetchFooterLinks } from '../utils/footerLinks';
import { getOrFetchNavigationLinks } from '../utils/navigationLinks';
import {
  getValidObject,
  getMapFromObjectArray,
  getUniqueValuesArray,
  resetDynamicObject,
} from '../utils/objectManipulation';
import { getProductsGroupedByCategory, getConcatProductsByCategory } from '../utils/products';

import pageStyle from './products/products.module.scss';
import styles from './search.module.scss';

const searchText = {
  el: {
    empty: 'Δεν βρέθηκαν αποτελέσματα για τον όρο ',
    emptyButton: 'Επιστροφή στην αρχική',
    products: 'Προϊόντα',
  },
};

export default function Search({ footerLinks, navigation, meta }) {
  const router = useRouter();
  const slug = [];

  const [deliveryMode, setDeliveryMode] = useState('');
  const [isCartVisible, setIsCartVisible] = useState(false);
  const [isForced, setIsForced] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [showBySelection, setShowBySelection] = useState(BANNER_SORT_OPTIONS.catalog);

  const [isFetching, setIsFetching] = useState(false);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const [groupedByCategoryProducts, setGroupedByCategoryProducts] = useState({});

  const [renderedCategories, setRenderedCategories] = useState([]);
  const [liveOffers, setLiveOffers] = useState([]);
  const [collectionTypes, setCollectionTypes] = useState([process.env.defaultCollectionType]);
  const [hasMore, setHasMore] = useState(true);
  const [categoriesMap, setCategoriesMap] = useState({});
  const [waveSpecialProductsGrouped, setWaveSpecialProductsGrouped] = useState([]);

  async function getResults(_collectionTypes = collectionTypes, _page = 0) {
    Loader.addEvent('GET_SEARCH_RESULTS');
    setIsFetching(true);
    setHasError(false);
    const results = await getSearchResults(
      router?.query?.product,
      _collectionTypes,
      router?.query?.category,
      _page,
      SortBy.getValue()
    );
    setIsFetching(false);
    Loader.removeEvent('GET_SEARCH_RESULTS');
    return results;
  }
  async function getResultsWrapper(forced = false, _collectionTypes = collectionTypes) {
    if (!router?.query?.product) return;

    if (forced) {
      const results = await getResults(_collectionTypes, 0);
      return forcedResults(results);
    }

    if (isFetching || totalPages <= page) {
      setHasMore(totalPages > page);
      return;
    }

    const results = await getResults(_collectionTypes, page);

    if (!isForced) {
      convertResults(results);
    }
  }

  function manipulateResults(
    isFirstFetch,
    results,
    prevCategories = [],
    prevCategoryProducts = {},
    prevLiveOffers = []
  ) {
    if (results.error) {
      resetAllState();
      setHasError(true);
      return;
    }

    setHasError(results.totalCount === 0);
    setTotalCount(results.totalCount);
    setTotalPages(results.totalPages);
    const productsByCategory = getProductsGroupedByCategory(results.data.products);
    const renderedCategoriesKeys = Object.keys(productsByCategory);
    const mergedCategoriesKeys = [...prevCategories, ...renderedCategoriesKeys];
    const uniqueMergedCategoriesKeys = getUniqueValuesArray(mergedCategoriesKeys);
    const concatProductsByCategory = getConcatProductsByCategory(prevCategoryProducts, productsByCategory);
    const mergedOffers = [...prevLiveOffers, ...results.data.offers];
    setLiveOffers(mergedOffers);
    findWaveProductsOffers(results.data.products, isFirstFetch);
    setGroupedByCategoryProducts(concatProductsByCategory);
    setRenderedCategories(uniqueMergedCategoriesKeys);
    setIsForced(false);
  }

  function forcedResults(results) {
    setPage(1);
    manipulateResults(true, results);
  }

  function convertResults(results) {
    setPage(page + 1);
    manipulateResults(false, results, renderedCategories, groupedByCategoryProducts, liveOffers);
  }

  function onChangeOrderBy(event) {
    SortBy.update(event.target.value);
    updateURL(event.target.value, router?.query?.product);
  }

  function onScrollProducts() {
    getResultsWrapper();
  }

  function updateURL(
    sortBy = router?.query?.sortBy,
    product = router?.query?.product,
    category = router?.query?.category
  ) {
    const params = {
      ...getValidObject('product', product),
      ...getValidObject('sortBy', sortBy),
      ...getValidObject('category', category),
    };

    router.push(
      {
        pathname: '/search',
        query: params,
      },
      undefined,
      { shallow: false }
    );
  }

  function resetAllState() {
    setLiveOffers([]);
    setWaveSpecialProductsGrouped([]);
    const resetGroupedByCategoryProducts = resetDynamicObject(groupedByCategoryProducts);
    setGroupedByCategoryProducts({
      ...groupedByCategoryProducts,
      ...resetGroupedByCategoryProducts,
    });
    setRenderedCategories([]);
    setTotalPages(1);
    setPage(0);
    setHasMore(true);
  }

  function findWaveProductsOffers(products, isFirstFetch) {
    if (!products.length) return;
    const productInCategory = products.filter(
      p => p.offerType === 'web-only-amount' || p.offerType === 'web-only-percentage'
    );
    const productsInCategory = isFirstFetch
      ? [...productInCategory]
      : [...waveSpecialProductsGrouped, ...productInCategory];
    setWaveSpecialProductsGrouped([...new Set(productsInCategory)]);
  }

  useEffect(() => {
    const categories$ = CategoryStore.subscribe(_categories => {
      if (!isEmpty(_categories)) {
        const categoriesNameMap = getMapFromObjectArray(_categories, '_id', 'name');
        setCategoriesMap(categoriesNameMap);
      }
    });
    const subscriptionDeliveryMode = DeliveryMode.subscribe(_mode => {
      setDeliveryMode(_mode);
      const _collectionTypes = DeliveryMode.getCollectionAsNumber(_mode);
      setCollectionTypes(_collectionTypes);
      setIsFetching(false);
      if (collectionTypes.toString() !== _collectionTypes.toString()) {
        resetAllState();

        setIsForced(true);
        // handle initial state update when collection types can change in a sec
        // starts with 900 and if store has local collection 900 & 9002 is updating
        // if we fire both requests without time out, we will know which one will respond last
        setTimeout(() => getResultsWrapper(true, _collectionTypes), 500);
      }
    });
    const sortBy$ = SortBy.subscribe(setShowBySelection);
    const isCartVisible$ = CartStore.getIsVisble$().subscribe(setIsCartVisible);
    return () => {
      categories$.unsubscribe();
      isCartVisible$.unsubscribe();
      sortBy$.unsubscribe();
      subscriptionDeliveryMode.unsubscribe();
    };
  }, []);

  useEffect(() => {
    SortBy.update(router?.query?.sortBy || 'default');
    ProductsSearchStore.updateQuery(router?.query?.product || '');
    resetAllState();
    if (router?.query?.product) {
      setIsForced(true);
      getResultsWrapper(true);
    }
  }, [router.isReady, router?.query?.sortBy, router?.query?.product, router?.query?.category]);

  if (!isBrowser()) return null;
  if (!router?.query?.product) return null;
  return (
    <>
      <CustomMetaHead meta={meta} />
      <div className={styles.headerContainer}>
        <Header navigation={navigation} isEnabled />
      </div>

      <Navigation navigation={navigation} />
      <DeliveryBanner />
      <CategoriesSelect slug={slug} />
      <div
        className={cn(pageStyle.mainContent, pageStyle.mainList, {
          [pageStyle.spaceOpenCart]: isCartVisible,
        })}
      >
        <div className={pageStyle.listBannerSearch}>
          <div className={pageStyle.sticky}>
            <CategoriesBanner
              onChangeOrderBy={onChangeOrderBy}
              showBySelection={showBySelection}
              categoryName={`Αναζήτηση για '${router?.query?.product}'`}
              deliveryMode={deliveryMode}
              isSearch
              isProductList
              term={router?.query?.product}
              hideOrderBy={!router?.query?.category}
              numOfProducts={totalCount}
              slug={slug}
              onlyProducts={!router?.query?.category && `${totalCount} ${searchText.el.products}`}
            />
          </div>
          <div className={pageStyle.mainContent}>
            <div className={styles.hideOnSmallScreen}>
              <div className={cn(pageStyle.sticky, pageStyle.categoriesAsideTop)}>
                <FavoriteAside />
                <CategoriesAside slug={slug} />
              </div>
            </div>
            <div className={styles.defaultContainer}>
              {(!isEmpty(liveOffers) || !isEmpty(waveSpecialProductsGrouped)) && (
                <div className={pageStyle.listSearch}>
                  <OfferList
                    offers={liveOffers}
                    showBySelection={showBySelection}
                    waveSpecialProductsGrouped={waveSpecialProductsGrouped}
                  />
                </div>
              )}
              {!isEmpty(renderedCategories) &&
                !isForced &&
                renderedCategories.map(renderedCategory => (
                  <div className={pageStyle.listSearch} key={renderedCategory}>
                    <div className={listTitle.listHeader}>
                      <h2 className={listTitle.listTitle}>{categoriesMap[renderedCategory]}</h2>
                    </div>
                    <InfiniteScroll
                      threshold={440} // 440px is the height of a category with 1 row of products
                      hasMore={hasMore}
                      // keep it like this until we purge the logic of multiple async calls to local db for products
                      initialLoad={false}
                      loader={<div key="0" />}
                      loadMore={onScrollProducts}
                      className={styles.infiniteScrollContainer}
                    >
                      {groupedByCategoryProducts[renderedCategory].map((product, index) => (
                        <ProductListItem
                          key={product._id}
                          _product={product}
                          productsQuantities={productsQuantities.getValue()[product._id]}
                          itemListId="Search"
                          itemListName="Search"
                          positionInList={index + 1}
                        />
                      ))}
                    </InfiniteScroll>
                  </div>
                ))}
              {isEmpty(renderedCategories) && hasError && (
                <div className={styles.imageContainer}>
                  <img
                    className={styles.img}
                    width="200"
                    height="200"
                    src={`/assets/${process.env.appName}/${process.env.appName}-empty-search.png`}
                    alt="empty results"
                  />
                  <p className={styles.emptyText}>{`${searchText.el.empty} "${router?.query?.product}"`}</p>
                  <Link href="/">
                    <a className={styles.link}>
                      <PrimaryButton>{searchText.el.emptyButton}</PrimaryButton>
                    </a>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer footerLinks={footerLinks} />
    </>
  );
}

export async function getStaticProps() {
  const [{ data: navigation }, footerLinks] = await Promise.all([getOrFetchNavigationLinks(), getOrFetchFooterLinks()]);
  const meta = META_DATA[process.env.appName].search;

  return {
    props: { navigation, footerLinks, meta },
  };
}
